import type { UploadRequestOptions, UploadRequest } from "./interface";

function getBody(xhr: XMLHttpRequest) {
  const text = xhr.responseText || xhr.response;
  if (!text) {
    return text;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const uploadRequest: UploadRequest = function (
  options: UploadRequestOptions
) {
  const {
    onProgress,
    onError,
    onSuccess,
    action,
    headers = {},
    name: originName,
    file,
    data: originData = {},
    withCredentials = false,
  } = options;

  function isGetValueFunction<T>(value: unknown): value is (f: File) => T {
    return typeof value === "function";
  }

  function getValueFromFile<T>(value: T | ((file: File) => T)): T {
    if (isGetValueFunction(value)) {
      return value(file);
    }
    return value;
  }

  const name = getValueFromFile(originName);
  const data = getValueFromFile(originData);
  const xhr = new XMLHttpRequest();
  if (onProgress && xhr.upload) {
    xhr.upload.onprogress = function (event: ProgressEvent) {
      let progress: number | undefined;
      if (event.total > 0) {
        progress = event.loaded / event.total;
      }
      onProgress(progress, event);
    };
  }
  xhr.onerror = function error(e) {
    onError(e);
  };
  xhr.onload = function onload() {
    if (xhr.status < 200 || xhr.status >= 300) {
      return onError(getBody(xhr));
    }

    onSuccess(getBody(xhr));
  };
  const formData = new FormData();
  if (data) {
    Object.keys(data).map((key) => formData.append(key, data[key]));
  }
  formData.append(name ?? "file", file);

  xhr.open("post", action, true);
  if (withCredentials && "withCredentials" in xhr) {
    xhr.withCredentials = true;
  }

  for (const h in headers) {
    // eslint-disable-next-line no-prototype-builtins
    if (headers.hasOwnProperty(h) && headers[h] !== null) {
      xhr.setRequestHeader(h, headers[h]);
    }
  }
  xhr.send(formData);
  return {
    abort() {
      xhr.abort();
    },
  };
};
